import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from "@angular/common/http";
import { catchError, Observable, retry, throwError, timer } from "rxjs";
import { AuthService } from "./auth.service";
import { environment } from "@app/env";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    req = this.authService.authenticate(AuthInterceptor.route(req));
    const currentLanguage = localStorage.getItem("lang") || "en";

    req = req.clone({
      setHeaders: {
        "Accept-Language": currentLanguage,
      },
    });
    let handler = next.handle(req);
    if (environment.httpRetry.enabled) {
      handler = handler.pipe(
        retry({
          count: environment.httpRetry.count,
          delay: (error, retryAttempt) => {
            return timer(
              retryAttempt * environment.httpRetry.delayInMilliseconds,
            );
          },
        }),
      );
    }

    return handler.pipe(
      catchError((error: HttpErrorResponse) => {
        if (AuthInterceptor.isTokenRelatedError(error))
          this.authService.logout();
        else return throwError(() => error);
      }),
    );
  }

  private static readonly tokenErrors: Array<string> = [
    "invalid token",
    "session must be authenticated first",
    "authorization header missing or invalid",
    "authHelper is not initialized",
  ];
  private static isTokenRelatedError(error: any): boolean {
    try {
      if (error.status === 401) return true;
      if (error.status === 500) {
        const message = (
          typeof error?.error === "string"
            ? error.error
            : typeof error?.message === "string"
              ? error.message
              : JSON.stringify(error)
        ).toLowerCase();

        for (let i = 0; i < this.tokenErrors.length; i++)
          if (message.includes(this.tokenErrors[i])) return true;
      }
    } catch {}
    return false;
  }
  private static route(req: HttpRequest<any>): HttpRequest<any> {
    if (req.url.startsWith("/assets/")) {
      return req;
    }

    if (/^(http|https):\/\//i.test(req.url)) {
      return req;
    }

    const cleanedUrl = req.url.replace(/^\/api\//, "").replace(/^\/+/, "");

    return req.clone({
      url: `${environment.apiUrl}${cleanedUrl}`,
    });
  }
}
